import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-black mb-3">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

const Privacy = () => (
  <div className="min-h-screen pt-32 pb-20 px-6 bg-white">
    <div className="max-w-3xl mx-auto">
      <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Legal</p>
      <h1 className="text-4xl md:text-5xl font-bold text-black mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-10">Last updated: May 2026</p>

      <Section title="Who we are">
        <p>
          Obsidian Chauffeurs (“we”, “us”, “our”) is a Manchester-based executive chauffeur service operating
          across the United Kingdom. We are the data controller for the personal information we collect.
          You can reach us at <a href="mailto:info@obsidianchauffeurs.co.uk" className="underline">info@obsidianchauffeurs.co.uk</a>.
        </p>
      </Section>

      <Section title="The information we collect">
        <p>When you request a quote, make a booking or create an account, we collect:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your name, email address and phone number</li>
          <li>Pickup and drop-off addresses, journey date and time</li>
          <li>Number of passengers, luggage, child seats and any special requirements</li>
          <li>Flight number (for airport transfers)</li>
          <li>Optional company name and billing details for corporate bookings</li>
          <li>Passenger details where you book on behalf of someone else</li>
          <li>Your account password (stored as a one-way hash — we never see it in plain text)</li>
        </ul>
      </Section>

      <Section title="How we use it">
        <ul className="list-disc pl-6 space-y-1">
          <li>To prepare your quote and provide the journey you have booked</li>
          <li>To send booking confirmations, updates, reminders and receipts (by email and SMS)</li>
          <li>To pass essential pickup details to the chauffeur assigned to your journey</li>
          <li>To improve our service and provide customer support</li>
          <li>Where required, to meet our legal and tax obligations</li>
        </ul>
      </Section>

      <Section title="Who we share it with">
        <p>We only share what's strictly needed, with trusted third parties:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>SendGrid (Twilio Inc.)</strong> — to deliver booking emails</li>
          <li><strong>Twilio</strong> — to send SMS updates to you and your chauffeur</li>
          <li><strong>Google Maps Platform</strong> — to autocomplete addresses and calculate routes</li>
          <li><strong>The assigned chauffeur</strong> — receives only the passenger contact details needed for the journey</li>
          <li>Payment processors will be added when card payments go live; we will update this policy accordingly</li>
        </ul>
        <p>We never sell your data, and we never share it for marketing by third parties.</p>
      </Section>

      <Section title="How long we keep it">
        <p>
          Booking records are retained for 6 years to meet UK accounting and tax requirements. You may ask
          us to delete your account at any time; any bookings tied to fulfilled financial transactions will
          be retained in anonymised form for the legal retention period.
        </p>
      </Section>

      <Section title="Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access a copy of the information we hold about you</li>
          <li>Have inaccurate information corrected</li>
          <li>Have your information deleted (subject to our legal retention obligations)</li>
          <li>Object to or restrict how we process it</li>
          <li>Lodge a complaint with the Information Commissioner's Office (ico.org.uk)</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:info@obsidianchauffeurs.co.uk" className="underline">info@obsidianchauffeurs.co.uk</a>.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          We use cookies to keep you signed in, remember your booking details so you don't have to
          re-enter them, and to keep the site secure. We will ask for your consent the first time you
          visit. You can change your mind at any time by clearing your browser's cookies for this site.
        </p>
      </Section>

      <Section title="Security">
        <p>
          Passwords are hashed using industry-standard algorithms. Account sessions use httpOnly cookies
          so they cannot be accessed by scripts in your browser. Communications between your browser
          and our servers are encrypted in transit using HTTPS.
        </p>
      </Section>
    </div>
  </div>
);

export default Privacy;
